// 📄 comunicacionService.ts
// Sub-fase K4 (refactor de Configuraciones Cloud/On-Premise): pool de
// Communication Users/Systems de S/4HANA Cloud, a nivel PROYECTO — ver
// Fase 0 de K4. Mismo patrón de acceso y de servicio que interfazZService.ts
// (Firestore directo desde el cliente, protegido por firestore.rules,
// lecturas puntuales con getDocs, sin onSnapshot).
//
// Nota de nombres de colección: la Fase 0 aprobó los paths
// "projects/{id}/comunicacion/users/{userId}" y ".../comunicacion/systems/
// {systemId}", pero esa forma no es una ruta válida de Firestore tal como
// está escrita (5 segmentos terminan en una COLECCIÓN "{userId}", no en un
// documento — haría falta un segmento más). Se implementa como el
// equivalente válido más cercano y consistente con el resto del código:
// 2 subcolecciones planas directas del proyecto, mismo estilo que
// transaccionesZ/badisZ/interfacesZ.
//
// Por ambiente (Fase 0 de K4, aprobado): cada User/System lleva su propia
// copia de datos por DEV/QAS/PRD, igual criterio que usa TODO el resto de
// esta app (DEV como ambiente donde se edita primero, QAS/PRD con su propia
// copia) — un mismo "Communication User" lógico puede tener un username
// distinto en cada ambiente real de SAP.
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { Ambiente } from "./solucionIntegrationService.ts";

export type TipoComunicacionUser = "inbound" | "outbound";

export interface ComunicacionUserEntorno {
  nombre: string; // username técnico en ese ambiente, ej. "ARIBA_MG"
  notas: string;
}

export interface ComunicacionUserData {
  tipo: TipoComunicacionUser;
  descripcion: string;
  entornos: Record<Ambiente, ComunicacionUserEntorno>;
}

export interface ComunicacionUser extends ComunicacionUserData {
  id: string;
  creadoPor: string;
  creadoEn: any;
  actualizadoEn: any;
}

export interface ComunicacionSystemEntorno {
  businessSystemId: string; // ej. "744749172-T" — hoy solo vivía como texto libre (ver Fase 0 de K3, 0.5)
  url: string;
  notas: string;
}

export interface ComunicacionSystemData {
  nombre: string; // ej. "ARIBACIG_SLP" — identificador lógico, no cambia por ambiente
  comunicacionUserId: string; // referencia a comunicacionUsers/{userId} del pool de ESTE proyecto
  descripcion: string;
  entornos: Record<Ambiente, ComunicacionSystemEntorno>;
}

export interface ComunicacionSystem extends ComunicacionSystemData {
  id: string;
  creadoPor: string;
  creadoEn: any;
  actualizadoEn: any;
}

const entornoVacioUser = (): ComunicacionUserEntorno => ({ nombre: "", notas: "" });
const entornoVacioSystem = (): ComunicacionSystemEntorno => ({ businessSystemId: "", url: "", notas: "" });

export const entornosVaciosUser = (): Record<Ambiente, ComunicacionUserEntorno> => ({
  DEV: entornoVacioUser(),
  QAS: entornoVacioUser(),
  PRD: entornoVacioUser()
});

export const entornosVaciosSystem = (): Record<Ambiente, ComunicacionSystemEntorno> => ({
  DEV: entornoVacioSystem(),
  QAS: entornoVacioSystem(),
  PRD: entornoVacioSystem()
});

const usersRef = (projectId: string) => collection(db, "projects", projectId, "comunicacionUsers");
const userRef = (projectId: string, id: string) => doc(db, "projects", projectId, "comunicacionUsers", id);
const systemsRef = (projectId: string) => collection(db, "projects", projectId, "comunicacionSystems");
const systemRef = (projectId: string, id: string) => doc(db, "projects", projectId, "comunicacionSystems", id);

// ─── Communication Users ────────────────────────────────────────────────

export const getComunicacionUsers = async (projectId: string): Promise<ComunicacionUser[]> => {
  const snap = await getDocs(usersRef(projectId));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<ComunicacionUser, "id">) }))
    .sort((a, b) => (a.entornos.DEV.nombre || "").localeCompare(b.entornos.DEV.nombre || ""));
};

const validarUser = (data: ComunicacionUserData) => {
  if (!data.tipo) throw new Error("El tipo (inbound/outbound) es obligatorio.");
  if (!data.entornos.DEV.nombre.trim()) throw new Error("El username en DEV es obligatorio.");
};

export const crearComunicacionUser = async (
  projectId: string,
  data: ComunicacionUserData,
  creadoPor: string
): Promise<string> => {
  validarUser(data);

  const ref = doc(usersRef(projectId));
  await setDoc(ref, {
    ...data,
    descripcion: data.descripcion.trim(),
    creadoPor,
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp()
  });
  return ref.id;
};

export const actualizarComunicacionUser = async (
  projectId: string,
  id: string,
  data: ComunicacionUserData
): Promise<void> => {
  validarUser(data);

  await updateDoc(userRef(projectId, id), {
    ...data,
    descripcion: data.descripcion.trim(),
    actualizadoEn: serverTimestamp()
  });
};

export const eliminarComunicacionUser = async (projectId: string, id: string): Promise<void> => {
  await deleteDoc(userRef(projectId, id));
};

// ─── Communication Systems ──────────────────────────────────────────────

export const getComunicacionSystems = async (projectId: string): Promise<ComunicacionSystem[]> => {
  const snap = await getDocs(systemsRef(projectId));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<ComunicacionSystem, "id">) }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre));
};

const validarSystem = (data: ComunicacionSystemData) => {
  if (!data.nombre.trim()) throw new Error("El nombre del System es obligatorio.");
  if (!data.comunicacionUserId) throw new Error("El Communication User asociado es obligatorio.");
};

export const crearComunicacionSystem = async (
  projectId: string,
  data: ComunicacionSystemData,
  creadoPor: string
): Promise<string> => {
  validarSystem(data);

  const ref = doc(systemsRef(projectId));
  await setDoc(ref, {
    ...data,
    nombre: data.nombre.trim(),
    descripcion: data.descripcion.trim(),
    creadoPor,
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp()
  });
  return ref.id;
};

export const actualizarComunicacionSystem = async (
  projectId: string,
  id: string,
  data: ComunicacionSystemData
): Promise<void> => {
  validarSystem(data);

  await updateDoc(systemRef(projectId, id), {
    ...data,
    nombre: data.nombre.trim(),
    descripcion: data.descripcion.trim(),
    actualizadoEn: serverTimestamp()
  });
};

export const eliminarComunicacionSystem = async (projectId: string, id: string): Promise<void> => {
  await deleteDoc(systemRef(projectId, id));
};
